'use client';

import { FilmScreening } from '@/app/[locale]/(rest)/films/[slug]/components/ScreeningList';
import { FilmShortProgramme } from '@/app/[locale]/(rest)/films/[slug]/page';
import { staticFilters } from '@/constants';
import { PathEnum } from '@/i18n/locale-config';
import { Link, useRouter } from '@/i18n/navigation';
import { Database } from '@/types/database.types';
import { formatDate } from '@/utils/date-format';
import { generateImagePath } from '@/utils/generate-image-path';
import { useTranslations } from 'next-intl';
import Image from '../Image';
import AddToCartButton from '../ui/AddToCartButton';

export type FilmListItemRow = Database['public']['Functions']['get_film_list_v2']['Returns'][0];
export type FilmListItem = Omit<FilmListItemRow, 'screenings' | 'short_programme'> & {
    screenings: FilmScreening[];
    short_programme: FilmShortProgramme;
};

const CONNEXE_TITLE = ['I+N x FMC / CMF SERIES', 'SÉRIES I+N x FMC / CMF'];

export default function FilmCard(props: FilmListItem) {
    const t = useTranslations();
    const router = useRouter();

    const {
        poster_url,
        title,
        synopsis,
        duration,
        slug,
        categories,
        screening_types,
        focuses,
        short_programme,
        screenings,
    } = props;

    const tags = Array.from(
        new Set([
            ...(Array.isArray(categories)
                ? categories.flatMap((item) =>
                      typeof item === 'object' && item ? Object.values(item) : item,
                  )
                : []),
            ...(Array.isArray(screening_types) ? screening_types : []),
        ]),
    );

    const focusesArray = Array.isArray(focuses)
        ? focuses
              .filter(
                  (focus): focus is { id: string; title: string; color: string | null } =>
                      typeof focus === 'object' &&
                      focus !== null &&
                      'id' in focus &&
                      'title' in focus,
              )
              .map((focus) => ({
                  id: focus.id,
                  title: focus.title,
                  color: focus.color,
              }))
        : [];
    const priorityFocus = focusesArray.find((focus) => CONNEXE_TITLE.includes(focus.title));
    const focusToDisplay = priorityFocus || focusesArray[0];

    const Icons = () => {
        const focusIds = focusesArray.map((focus) => focus.id);

        const filteredIcons = staticFilters('card').filter((filter) =>
            focusIds.includes(filter.id),
        );

        return (
            <div className="absolute -top-1 -left-1 flex flex-col z-10">
                {filteredIcons.map((filter) => (
                    <Image
                        key={filter.id}
                        className="lg:w-[60px] lg:h-[60px] w-10 h-10"
                        src={filter.icon}
                        alt={`${filter.name.en} Icon`}
                        width={24}
                        height={24}
                    />
                ))}
            </div>
        );
    };

    return (
        <div className="relative group flex flex-col lg:gap-7 gap-5 w-full h-full">
            <Link
                className="group flex flex-col lg:gap-7 gap-5 w-full text-white"
                href={PathEnum.Films + slug}
                target="_blank"
            >
                <>
                    <span className="w-full lg:h-[352px] h-[220px] relative bg-slate-400 overflow-hidden">
                        <Image
                            className="h-full object-cover object-center w-full"
                            src={generateImagePath(poster_url)}
                            alt="Poster"
                            fill
                            forceSrc
                            style={{
                                border: focusToDisplay
                                    ? `4px solid ${focusToDisplay.color}`
                                    : `none`,
                            }}
                        />
                        <span className="absolute bottom-5 left-5 gap-2 flex flex-wrap-reverse">
                            {tags.map((tag, index) => (
                                <span
                                    className="text-md lg:text-[25px] p-1 border border-white leading-none uppercase text-nowrap"
                                    key={index}
                                >
                                    {tag as String}
                                </span>
                            ))}
                        </span>
                        <Icons />
                    </span>
                    <span className="flex flex-col gap-3">
                        <span className="flex justify-between max-lg:flex-col-reverse gap-3">
                            <span className="text-h5 font-normal hover:text-primary group-hover:text-primary transition-all ease-in-out duration-300 ease text-white group lg:text-[46px] uppercase w-full leading-tight line-clamp-3">
                                {title}
                            </span>
                            {(short_programme || focusToDisplay) && (
                                <span className="lg:text-[25px] text-md uppercase lg:w-3/6 w-full flex flex-col leading-none text-gray-200 lg:text-right gap-2">
                                    {!focusToDisplay &&
                                        short_programme &&
                                        typeof short_programme === 'object' &&
                                        'title' in short_programme && (
                                            <span>[{String(short_programme.title)}]</span>
                                        )}
                                    {focusToDisplay && (
                                        <span
                                            style={{
                                                color: focusToDisplay.color || 'inherit',
                                            }}
                                        >
                                            [{focusToDisplay.title}]
                                        </span>
                                    )}
                                </span>
                            )}
                        </span>
                        <span className="flex justify-between gap-4 items-end mt-2">
                            <span className="text-gray-200/50 w-2/6 leading-none font-helvetica-neue">{`${duration} ${t('Common.minutes')}`}</span>
                            <span className="w-4/6 uppercase font-helvetica-neue text-tofu text-[12px] lg:text-md font-light line-clamp-1 items-end text-right leading-none flex gap-5 lg:gap-7 justify-end">
                                {screenings &&
                                    screenings.map((screening, index) => {
                                        if (!screening.start_time) {
                                            return null;
                                        }
                                        return (
                                            <span className="inline-block" key={index}>
                                                {formatDate(screening.start_time)}
                                            </span>
                                        );
                                    })}
                            </span>
                        </span>
                    </span>
                    <p className="font-helvetica-neue font-light text-[14px] lg:text-[22px] text-gray-200 line-clamp-3 leading-tight">
                        {synopsis}
                    </p>
                </>
            </Link>
            <div className="mt-auto">
                <AddToCartButton film={props} />
            </div>
        </div>
    );
}
